import { DateTime } from "luxon";
import type { GenerateConfig } from "rc-picker/lib/generate";

/**Convert Moment.js/Ant Design format strings to Luxon format tokens based on the mapping from luxonMomentAdapter.ts*/
const convertFormatToLuxon = (formatStr: string): string => {
  return formatStr
    .replace(/YYYY/g, "yyyy")
    .replace(/DD/g, "dd")
    .replace(/dddd/g, "EEEE")
    .replace(/ddd/g, "EEE")
    .replace(/MMMM/g, "LLLL")
    .replace(/MMM/g, "LLL")
    .replace(/MM/g, "LL")
    .replace(/M/g, "L")
    .replace(/D/g, "d")
    .replace(/HH/g, "HH")
    .replace(/H/g, "H")
    .replace(/hh/g, "hh")
    .replace(/h/g, "h")
    .replace(/mm/g, "mm")
    .replace(/m/g, "m")
    .replace(/ss/g, "ss")
    .replace(/s/g, "s")
    .replace(/A/g, "a")
    .replace(/a/g, "a");
};

const luxonGenerateConfig: GenerateConfig<DateTime> = {
  getNow: () => DateTime.now(),

  getFixedDate: (string: string) => DateTime.fromISO(string),

  getEndDate: (date: DateTime) => date.endOf("day"),

  getYear: (date: DateTime) => date.year,
  getMonth: (date: DateTime) => date.month - 1,
  getDate: (date: DateTime) => date.day,
  getWeekDay: (date: DateTime) => date.weekday % 7,

  getHour: (date: DateTime) => date.hour,
  getMinute: (date: DateTime) => date.minute,
  getSecond: (date: DateTime) => date.second,

  addYear: (date: DateTime, diff: number) => date.plus({ years: diff }),
  addMonth: (date: DateTime, diff: number) => date.plus({ months: diff }),
  addDate: (date: DateTime, diff: number) => date.plus({ days: diff }),

  setYear: (date: DateTime, year: number) => date.set({ year }),
  setMonth: (date: DateTime, month: number) => date.set({ month: month + 1 }), // rc-picker sends 0-based
  setDate: (date: DateTime, day: number) => date.set({ day }),
  setHour: (date: DateTime, hour: number) => date.set({ hour }),
  setMinute: (date: DateTime, minute: number) => date.set({ minute }),
  setSecond: (date: DateTime, second: number) => date.set({ second }),

  isAfter: (date1: DateTime, date2: DateTime) => date1 > date2,
  isValidate: (date: DateTime) => date.isValid,

  locale: {
    getWeekFirstDay: () => 0, // Sunday = 0
    getWeekFirstDate: (locale: string, date: DateTime) => date.startOf("week"),
    getWeek: (locale: string, date: DateTime) => date.weekNumber,
    getShortWeekDays: () => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    getShortMonths: () => [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    format: (locale: string, date: DateTime, format: string) => {
      const luxonFormat = convertFormatToLuxon(format);
      return date.toFormat(luxonFormat);
    },
    parse: (locale: string, text: string, formats: string[]) => {
      for (const format of formats) {
        const luxonFormat = convertFormatToLuxon(format);
        const parsed = DateTime.fromFormat(text, luxonFormat);
        if (parsed.isValid) {
          return parsed;
        }
      }
      // Try ISO format as fallback
      const isoDate = DateTime.fromISO(text);
      return isoDate.isValid ? isoDate : null;
    },
  },
};

export default luxonGenerateConfig;
